import { DateTime } from 'luxon'
import React, { MouseEvent, ReactNode, forwardRef, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import translationKeys from '@translationKeys'
import { Nullable } from '@types'

import { CustomTooltip } from '@components/CustomTooltip'
import EventNameWithArrow from '@components/EventNameWithArrow'
import LinearProgressWithLabel from '@components/LinearProgressWithLabel'
import TruncateLines from '@components/TruncateLines'
import Button from '@components/formControls/Button'
import CustomRoundCheckbox from '@components/formControls/CustomRoundCheckbox'
import ProfileAlertButton from '@modules/alerts/components/ProfileAlertButton'
import useAlertDataForProfiles from '@modules/alerts/hooks/useAlertDataForProfiles'
import { getProfileIsUpdated } from '@modules/alerts/utils'
import { useAuthContext } from '@modules/auth/hooks/useAuthContext'
import { isP1PlusEcommerceUser, isP1PlusNotFreeUser } from '@modules/auth/types'
import ContactAttendeesPopover from '@modules/chat/components/ContactAttendeesPopover'
import ContactedBubble from '@modules/chat/components/ContactedBubble'
import { ChatType } from '@modules/chat/types'
import { useChatContext } from '@modules/chat/useChatContext'
import ContactsHistoryPopover from '@modules/contacts/components/ContactsHistoryPopover'
import useContactWithProfilesCounts from '@modules/contacts/hooks/useContactWithProfilesCounts'
import { Meeting } from '@modules/contacts/types'
import useFetchProfile from '@modules/profile/hooks/useFetchProfile'
import { BIO_DEV_PHASE_ITEMS } from '@modules/search/components/ResultsSection/constants'
import { Delegate, ProfileType } from '@modules/search/types'
import CustomColumnDropdown from '@modules/shortlist/components/ShortlistTable/CustomColumnPopover/CustomColumnDropdown'
import {
  CustomColumnType,
  DEFAULT_SHORTLIST_SORT,
  ShortListCustomField,
  ShortlistCategoriesConfigProps,
  ShortlistExtendedProfile,
  ShortlistProfilePriority,
  ShortlistSort,
  ShortlistSortOrder,
  ShortlistTableConfigProps,
  ShortlistTableHeadProps,
  ShortlistTableProps,
  ShortlistTableRowProps,
} from '@modules/shortlist/types'
import { Box, Link } from '@mui/material'
import Paper from '@mui/material/Paper'
import Skeleton from '@mui/material/Skeleton'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import { DRAG_AUTO_SCROLL_PX_AMOUNT, TOP_SCROLL_THRESHOLD } from '@shared/constants'
import { useFeatureFlags } from '@shared/hooks/useFeatureFlags'
import { calcStageProgress } from '@shared/utils'
import { dateTimeFormats } from '@theme/variables'

import ContactCountsCell from './ContactCountsCell'
import CustomColumnHeadCell from './CustomColumnHeadCell'
import CustomTagCell from './CustomTagCell'
import DraggableTableRow from './DraggableTableRow'
import EventsWithPopover from './EventsWithPopover'
import ProfileTitleCell from './ProfileTitleCell'
import ProfileTitleHeadCell from './ProfileTitleHeadCell'
import ProfileTitleTagHeadCell from './ProfileTitleTagHeadCell'
import SelectPriority from './SelectPriority'
import TableHeadCell from './TableHeadCell'
import {
  ContactButtonWrapper,
  CustomColumnCellContent,
  NoteButtonLabel,
  ProfileDateTitle,
  StyledNoteButton,
  StyledTableCell,
  TableWrapper,
  TitleHeading,
} from './index.styles'

const tableColumnsConfig: ShortlistTableConfigProps[] = [
  {
    fieldName: 'profileTitle',
    isEnabled: true,
    restCellProps: { $cellWidth: 410, $isNameCell: true, $fixedWidth: true },
    isDragHandle: true,
    isSortable: true,
    customDisable: true,
  },
  {
    fieldName: 'company',
    isEnabled: false,
    columnTitle: translationKeys.CUSTOM_LIST_TABLE_HEADER_COMPANY_NAME,
    restCellProps: { $cellWidth: 130, $fixedWidth: true },
    isSortable: true,
  },
  {
    fieldName: 'lastEvent',
    isEnabled: false,
    columnTitle: translationKeys.CUSTOM_LIST_TABLE_HEADER_LAST_EVENT,
    restCellProps: { $cellWidth: 130, $fixedWidth: true },
    isSortable: true,
  },
  {
    fieldName: 'futureEvents',
    isEnabled: false,
    columnTitle: translationKeys.CUSTOM_LIST_TABLE_HEADER_FUTURE_EVENTS,
    restCellProps: { $cellWidth: 145, $fixedWidth: true },
    isSortable: true,
  },
  {
    fieldName: 'contactStatus',
    isEnabled: false,
    columnTitle: translationKeys.CUSTOM_LIST_TABLE_HEADER_CONTACT_STATUS,
    restCellProps: { $cellWidth: 190, $fixedWidth: true, $nowrap: true },
    isSortable: false,
  },
  {
    fieldName: 'updatedAt',
    isEnabled: false,
    columnTitle: translationKeys.CUSTOM_LIST_TABLE_HEADER_UPDATED,
    restCellProps: { $cellWidth: 107, $fixedWidth: true, $nowrap: true },
    isSortable: true,
  },
  {
    fieldName: 'createdAt',
    isEnabled: false,
    columnTitle: translationKeys.CUSTOM_LIST_TABLE_HEADER_DATE_ADDED,
    restCellProps: { $cellWidth: 107, $fixedWidth: true, $nowrap: true },
    isSortable: true,
  },
  {
    fieldName: 'nda',
    columnTitle: translationKeys.CUSTOM_LIST_TABLE_HEADER_NDA,
    restCellProps: { $cellWidth: 80, $fixedWidth: true },
    isSortable: true,
  },
  {
    fieldName: 'priority',
    columnTitle: translationKeys.CUSTOM_LIST_TABLE_HEADER_PRIORITY,
    restCellProps: { $cellWidth: 145, $fixedWidth: true },
    isSortable: true,
  },
  {
    fieldName: 'tagId',
    columnTitle: translationKeys.CUSTOM_LIST_TABLE_HEADER_CUSTOM_TAG,
    restCellProps: { $cellWidth: 200, $fitContent: true },
    isSortable: true,
  },
  {
    fieldName: 'note',
    columnTitle: translationKeys.CUSTOM_LIST_TABLE_HEADER_NOTES,
    restCellProps: { $cellWidth: 200 },
    isSortable: true,
  },
  {
    fieldName: 'phase',
    isEnabled: false,
    columnTitle: translationKeys.CUSTOM_LIST_TABLE_HEADER_PHASE,
    restCellProps: { $cellWidth: 145, $fixedWidth: true, $nowrap: true },
    isSortable: true,
  },
  {
    fieldName: 'stretchColumn',
    isEnabled: true,
    columnTitle: '',
    restCellProps: { $cellWidth: 1, $noPadding: true },
  },
  {
    fieldName: 'profileContact',
    isEnabled: true,
    restCellProps: { $isContactCell: true, $cellWidth: 190, $fixedWidth: true },
  },
]

const ShortlistOverviewTableColumnHeaders = (
  props: ShortlistTableHeadProps & {
    setCustomFieldSortId: (id?: number) => void
    customFieldSortId?: number
  },
) => {
  const {
    optionalColumnsConfig,
    allProfilesSelected,
    onSelectAllProfiles,
    sort,
    onSortChange,
    tableColumns,
    activeShortlistId,
    activeShortlist,
    setCustomFieldSortId,
    customFieldSortId,
    onEditCustomColumnButtonClick,
    updateConfig,
  } = props

  const { t } = useTranslation()
  const [fieldWithActiveSort, setFieldWithActiveSort] = useState<string | undefined>(DEFAULT_SHORTLIST_SORT.fieldName)
  const [showEditCustomTagsPopup, setShowEditCustomTagsPopup] = useState(false)

  useEffect(() => {
    setFieldWithActiveSort(sort.fieldName)
  }, [])
  const handleSortChange = useCallback(({ fieldName, sortOrder }: ShortlistSort) => {
    if (!sortOrder) {
      handleSortChange(DEFAULT_SHORTLIST_SORT)
      return
    }
    setCustomFieldSortId(undefined)
    setFieldWithActiveSort(fieldName as string)
    onSortChange({ fieldName, sortOrder })
    onSelectAllProfiles(false)
  }, [])

  const handleCustomSortChange = useCallback(
    ({ customFieldId, sortOrder }: { customFieldId?: number; sortOrder?: ShortlistSortOrder }) => {
      if (!sortOrder) {
        handleSortChange(DEFAULT_SHORTLIST_SORT)
        return
      }

      setFieldWithActiveSort(undefined)
      setCustomFieldSortId(customFieldId)
      onSortChange({ fieldName: 'customFieldValues', sortOrder })
      onSelectAllProfiles(false)
    },
    [],
  )

  // @TODO: refactor, use just generic "render" method
  const tableHeaderColumnsComponents: Record<ShortlistTableConfigProps['fieldName'], () => ReactNode> = {
    // eslint-disable-next-line react/no-unstable-nested-components
    profileTitle: () => (
      <ProfileTitleHeadCell
        allProfilesSelected={allProfilesSelected}
        onSelectAllProfiles={onSelectAllProfiles}
        sortOrder={sort?.sortOrder}
        isSortActive={!!sort && fieldWithActiveSort === 'profileTitle'}
      />
    ),
    // eslint-disable-next-line react/no-unstable-nested-components
    tagId: () => (
      <ProfileTitleTagHeadCell
        activeShortlistId={activeShortlistId}
        showEditCustomTagsPopup={showEditCustomTagsPopup}
        setShowEditCustomTagsPopup={setShowEditCustomTagsPopup}
        sortOrder={sort?.sortOrder}
        isSortActive={!!sort && fieldWithActiveSort === 'tagId'}
      />
    ),
    // eslint-disable-next-line react/no-unstable-nested-components
    profileContact: () => <TitleHeading variant="h3">{t(translationKeys.CUSTOM_LIST_TABLE_HEADER_CONTACT)}</TitleHeading>,
  }

  const customColumnHeaderComponent = (customField?: ShortListCustomField) => {
    return (
      <CustomColumnHeadCell
        key={customField?.id}
        onEditCustomColumnButtonClick={onEditCustomColumnButtonClick}
        sortOrder={sort?.sortOrder}
        isSortActive={customFieldSortId === customField?.id}
        headerTitle={customField?.fieldName}
        customFieldId={customField?.id}
        onSortChange={handleCustomSortChange}
      />
    )
  }

  // simulate auto-scroll to top for browsers that dont support it and make smoother for others
  useEffect(() => {
    const eventHandler = (e: DragEvent) => {
      const top = e.clientY

      if (top < TOP_SCROLL_THRESHOLD && top > 0) {
        window.scrollTo(0, window.scrollY - DRAG_AUTO_SCROLL_PX_AMOUNT)
      }
    }

    window.addEventListener('dragover', eventHandler)

    return () => {
      window.removeEventListener('dragover', eventHandler)
    }
  }, [])

  return (
    <TableRow>
      {tableColumns.map((tableColumn: ShortlistTableConfigProps) => {
        const { columnTitle, restCellProps, fieldName, isEnabled, isSortable } = tableColumn

        const customField = activeShortlist?.customFields?.find(field => field?.fieldName === tableColumn?.fieldName)

        const cellComponent = customField
          ? customColumnHeaderComponent
          : tableHeaderColumnsComponents[fieldName as keyof ShortlistTableConfigProps]

        if (
          !(
            isEnabled ||
            !!customField ||
            (optionalColumnsConfig && optionalColumnsConfig[fieldName as keyof ShortlistCategoriesConfigProps]?.visible)
          )
        )
          return null

        const isSortActive = fieldWithActiveSort === fieldName

        return (
          <TableHeadCell
            key={fieldName}
            fieldName={fieldName}
            restCellProps={restCellProps}
            cellComponent={cellComponent}
            columnTitle={columnTitle}
            isSortable={isSortable}
            isSortActive={isSortActive}
            sortOrder={sort?.sortOrder}
            updateConfig={updateConfig}
            optionalColumnsConfig={optionalColumnsConfig}
            customField={customField}
            onSortChange={handleSortChange}
          />
        )
      })}
    </TableRow>
  )
}
const getShortlistOverviewTableColumns = (props: ShortlistTableRowProps) => {
  const {
    contactsOverview,
    profileData,
    optionalColumnsConfig,
    selectedRowIds,
    onSelectProfile,
    onClickProfile,
    onChangeNda,
    onChangePriority,
    onTagUpdate,
    onNoteClick,
    onCustomColumnClick,
    onContactClick,
    onContactStatusClick,
    alertsShortlistId,
    profileAlert,
    tableColumns,
    hasAccessToAlerts,
    hasAccessToChat,
    isEcommerce,
    attendingEvents,
    priorityOptions,
    t,
    activeShortlist,
  } = props
  const {
    profileStatus,
    profileId,
    profileUpdatedDate,
    nda,
    note,
    profileEventName,
    profileEventStartDate,
    profileEventEndDate,
    profileDevPhase,
    tagId,
    companyName,
    companyId,
    profileImgData,
    profileType,
    profileLatestCompany,
    profileEvents,
    priority,
    profileHasPublicEvent,
    customFieldValues,
  } = profileData

  const isProfileActive = profileStatus === 'active'

  const isProfileUpdated = getProfileIsUpdated({
    profile: profileImgData,
    profileAlert,
    type: profileType,
    assetLatestCompany: profileLatestCompany,
    events: profileEvents,
  })

  const isUpdated = hasAccessToAlerts && (isProfileUpdated || !!profileAlert?.hasNewActivity)
  const tableColumnsComponents: Record<ShortlistTableConfigProps['fieldName'], () => ReactNode> = {
    profileTitle: () => (
      <ProfileTitleCell
        profileData={profileData}
        isPrivate={profileImgData?.isPrivate}
        isSelectedProfile={selectedRowIds.includes(profileId)}
        onSelectProfile={onSelectProfile}
        onClickProfile={onClickProfile}
        isDisabled={!isProfileActive}
      />
    ),
    lastEvent: () => {
      if (!profileData.profileEvents || profileData.profileEvents.length === 0) {
        return null
      }
      const now = DateTime.now()
      const pastEvents = profileData.profileEvents.filter(
        event => DateTime.fromISO(event.endDate).startOf('day') < now.startOf('day'),
      )

      const sortedEvents = pastEvents.sort(
        (a, b) => DateTime.fromISO(b.endDate).valueOf() - DateTime.fromISO(a.endDate).valueOf(),
      )
      const lastEvent = sortedEvents[0]

      if (!lastEvent) return null

      return (
        <TruncateLines lines={5}>
          <Typography style={{ fontWeight: 700 }}>{lastEvent.name}</Typography>
        </TruncateLines>
      )
    },
    futureEvents: () => (
      <EventsWithPopover profileData={profileData} attendingEvents={attendingEvents} isSubscriber={hasAccessToAlerts} />
    ),
    profileEventName: () => (
      <EventNameWithArrow eventName={profileEventName} startDate={profileEventStartDate} endDate={profileEventEndDate} />
    ),
    contactStatus: () =>
      contactsOverview && isProfileActive ? (
        <ContactCountsCell
          companyId={profileData.companyId}
          contactsOverview={contactsOverview}
          onExpandClick={ref => onContactStatusClick(ref, profileData.companyId)}
        />
      ) : null,
    phase: () => {
      if (!profileDevPhase && profileDevPhase !== '') return null

      const value = profileDevPhase || 'N/A'

      return <LinearProgressWithLabel label={value} value={calcStageProgress(BIO_DEV_PHASE_ITEMS, value)} orientation="column" />
    },
    company: () => {
      if (!companyName) {
        return null
      }

      return (
        <CustomTooltip title={companyName} placement="top" arrow={false} padding="5">
          <Link
            href={`/plus/profile/${companyId}/company`}
            underline="none"
            style={{ wordBreak: 'break-word', fontWeight: '700' }}
          >
            <TruncateLines lines={5}>{companyName}</TruncateLines>
          </Link>
        </CustomTooltip>
      )
    },
    updatedAt: () => (
      <div>
        {isUpdated ? (
          <ProfileDateTitle>
            <Trans>{translationKeys.UPDATED}</Trans>
          </ProfileDateTitle>
        ) : null}
        {DateTime.fromISO(profileUpdatedDate).toFormat(dateTimeFormats.eventDateFormat)}
      </div>
    ),
    nda: () => <CustomRoundCheckbox checked={!!nda} onChange={e => onChangeNda(profileId, e.target?.checked)} />,
    createdAt: () => <div>{DateTime.fromISO(profileData.createdAt).toFormat(dateTimeFormats.eventDateFormat)}</div>,
    tagId: () => <CustomTagCell tagId={tagId} onTagUpdate={newTagId => onTagUpdate(profileId, newTagId)} />,
    note: () => (
      <StyledNoteButton variant="text" onClick={e => onNoteClick(e, { profileId, note: note ?? '' })}>
        <TruncateLines lines={5}>
          {note || (
            <NoteButtonLabel>
              <Trans>{translationKeys.ADD_NOTE_HERE}</Trans>
            </NoteButtonLabel>
          )}
        </TruncateLines>
      </StyledNoteButton>
    ),
    priority: () => (
      <SelectPriority
        key={priority}
        label={t(translationKeys.CUSTOM_LIST_TABLE_HEADER_PRIORITY)}
        priority={priority ?? ShortlistProfilePriority.NOT_SET}
        priorityOptions={priorityOptions}
        onSelect={(selectedPriority: ShortlistProfilePriority) => onChangePriority(profileId, selectedPriority)}
      />
    ),
    profileContact: () => {
      if (profileImgData?.isPrivate && !profileHasPublicEvent) {
        return null
      }

      return (
        <>
          {hasAccessToAlerts && (
            <ProfileAlertButton
              alertsShortlistId={alertsShortlistId}
              profile={{ id: profileData.globalId, '@type': profileData.profileType }}
              savedProfileAlert={profileAlert}
            />
          )}
          {hasAccessToChat && !isEcommerce && (
            <ContactButtonWrapper>
              <ContactedBubble hasContacted={profileData.hasContacted}>
                <Button onClick={e => onContactClick(e, profileData)} color="primary" variant="contained" size="small">
                  <Trans>{translationKeys.CUSTOM_LIST_TABLE_CONTACT_BUTTON}</Trans>
                </Button>
              </ContactedBubble>
            </ContactButtonWrapper>
          )}
        </>
      )
    },
  }

  const getCustomColumnsComponents = () => {
    return activeShortlist?.customFields
      ?.filter(customColumn => activeShortlist.columns?.[customColumn.id as number])
      .reduce((acc, item) => {
        const fieldType = item?.fieldType

        return {
          ...acc,
          [item?.fieldName as string]: () => (
            <CustomColumnCellContent>
              {fieldType === CustomColumnType.FREE_TEXT ? (
                <StyledNoteButton
                  variant="text"
                  onClick={e =>
                    onCustomColumnClick(e, {
                      profileId,
                      fieldId: item.id as number,
                      customFieldValue: customFieldValues?.[item.id as number] ?? '',
                    })
                  }
                >
                  {customFieldValues?.[item.id as number] ? (
                    <TruncateLines lines={5}>{customFieldValues?.[item.id as number]}</TruncateLines>
                  ) : (
                    <NoteButtonLabel>
                      <Trans>{translationKeys.ADD_TEXT_HERE}</Trans>
                    </NoteButtonLabel>
                  )}
                </StyledNoteButton>
              ) : (
                <CustomColumnDropdown
                  shortlistId={activeShortlist.id}
                  profileId={profileId}
                  customFieldId={item.id as number}
                  availableOptions={item.fieldOptions}
                  columnName={item.fieldName || ''}
                  selectedOptionId={customFieldValues?.[item.id as number] ?? ''}
                  onOptionSelect={(selectedProfileId, fieldId, value) =>
                    onCustomColumnClick(null, {
                      profileId: selectedProfileId,
                      fieldId,
                      customFieldValue: value,
                    })
                  }
                />
              )}
            </CustomColumnCellContent>
          ),
        }
      }, {})
  }
  const customColumnsComponents = getCustomColumnsComponents()

  const columnsComponents = {
    ...tableColumnsComponents,
    ...customColumnsComponents,
  }

  return (
    <DraggableTableRow
      key={profileId}
      tableColumnsConfig={tableColumns}
      optionalColumnsConfig={optionalColumnsConfig}
      tableColumnsComponents={columnsComponents}
      profileId={profileId}
      profileUpdatedDate={profileUpdatedDate}
      profileData={profileData}
      isActive={isProfileActive}
      isHighlighted={isUpdated}
      highlightedRowLabel={translationKeys.UPDATED}
      activeShortlist={activeShortlist}
    />
  )
}

const ShortlistTable = forwardRef<HTMLDivElement, ShortlistTableProps>((props: ShortlistTableProps, ref) => {
  const { t } = useTranslation()
  const {
    activeShortlistId,
    isPageDataLoading = false,
    profilesPageData,
    optionalColumnsConfig,
    selectedRowIds,
    onSelectProfile,
    onClickProfile,
    onEditNote,
    onEditCustomColumn,
    onChangeNda,
    onChangePriority,
    onTagUpdate,
    sort,
    onSortChange,
    alertsShortlistId,
    setCustomFieldSortId,
    customFieldSortId,
    activeShortlist,
    handleEditCustomColumnSuccess,
    handleCloseEditCustomColumnPopover,
    onEditCustomColumnButtonClick,
    updateConfig,
  } = props
  const [contactStatusDetailsPopoverRef, setContactStatusDetailsPopoverRef] = useState<Nullable<HTMLElement>>(null)
  const [contactStatusDetailsCompanyId, setContactStatusDetailsCompanyId] = useState<Nullable<number>>(null)
  const { openChatModal } = useChatContext()

  const hasBiotechProfiles = useMemo(
    () => profilesPageData.some(profile => profile.profileType === ProfileType.biotech),
    [profilesPageData],
  )

  const { user } = useAuthContext()
  const isSubscriber = isP1PlusNotFreeUser(user?.subscriptionLevel || 0)
  const isEcommerce = isP1PlusEcommerceUser(user?.subscriptionLevel || 0)
  const { hasAccessToChat, hasAccessToAlerts } = useFeatureFlags()
  const companyContactsMap = useContactWithProfilesCounts({ companyIds: profilesPageData.map(i => i.companyId) })

  const tableColumns = useMemo(() => {
    let columns = [...tableColumnsConfig]

    if (!isSubscriber && !isEcommerce) {
      columns = columns.filter(column => column.fieldName !== 'phase')
    }

    if (!isSubscriber && !isEcommerce) {
      columns = columns.filter(column => column.fieldName !== 'company')
    }

    if (isEcommerce) {
      columns = columns.filter(column => column.fieldName !== 'profileContact')
    }
    if (activeShortlist?.customFields) {
      activeShortlist?.customFields.sort((a, b) => (a.id || 0) - (b.id || 0))
      const customColumns = activeShortlist?.customFields
        .filter(customField => activeShortlist.columns?.[customField.id as number]?.visible)
        .map(field => ({
          columnTitle: field.fieldName,
          fieldName: field.fieldName || '',
          isSortable: true,
          restCellProps: { $cellWidth: activeShortlist.columns?.[field.id as number]?.width ?? 200, $fixedWidth: true },
        }))

      const statusesColumnIndex = columns.findIndex(column => column.fieldName === 'tagId')

      columns.splice(statusesColumnIndex + 1, 0, ...customColumns)
    }

    return columns
  }, [profilesPageData, isSubscriber, tableColumnsConfig, hasBiotechProfiles, activeShortlist, companyContactsMap])

  const tableElementRef = useRef<HTMLTableElement>(null)

  const [contactAttendeesPopoverData, setContactAttendeesPopoverData] = useState<
    Nullable<{
      anchor: Nullable<HTMLElement>
      profile: ShortlistExtendedProfile
    }>
  >(null)

  const { data: fetchedProfile } = useFetchProfile({
    id: contactAttendeesPopoverData?.profile.globalId,
    type: contactAttendeesPopoverData?.profile.profileType || ProfileType.delegate,
    disabled: !contactAttendeesPopoverData?.profile,
  })

  const profiles = useMemo(
    () => profilesPageData.map(profilePageData => ({ id: profilePageData.globalId, '@type': profilePageData.profileType })),
    [profilesPageData],
  )

  const { profileAlerts, profilesAlertsMap } = useAlertDataForProfiles(profiles, isSubscriber && !!profiles.length)
  const onContactClick = (e: MouseEvent<HTMLElement>, profile: ShortlistExtendedProfile) => {
    if (profile.profileType === ProfileType.delegate) {
      return openChatModal({
        delegates: [profile.profileImgData as unknown as Delegate],
        companyId: profile.companyId,
        chatType: ChatType.DIRECT,
      })
    }

    if (e?.currentTarget) {
      setContactAttendeesPopoverData({ anchor: e?.currentTarget, profile })
      return null
    }

    return setContactAttendeesPopoverData(null)
  }

  const onContactStatusClick = (popoverRef: Nullable<HTMLElement>, companyId?: number) => {
    setContactStatusDetailsPopoverRef(popoverRef)
    setContactStatusDetailsCompanyId(companyId || null)
  }

  const onNoteClick = useCallback((e: Nullable<MouseEvent<HTMLElement>>, data?: { profileId: number; note: string }) => {
    onEditNote(e, data)
  }, [])

  const onCustomColumnClick = useCallback(
    (e: Nullable<MouseEvent<HTMLElement>>, data?: { profileId: number; fieldId: number; customFieldValue: string }) => {
      onEditCustomColumn(e, data)
    },
    [],
  )

  const onSelectAllProfiles = (allSelected: boolean) => {
    onSelectProfile(allSelected ? profilesPageData.map(p => p.profileId) : [])
  }

  const allProfilesSelected = useMemo(() => {
    return profilesPageData.length > 0 && profilesPageData.every(i => selectedRowIds.includes(i.profileId))
  }, [selectedRowIds, profilesPageData])

  const priorityOptions = useMemo(
    () => [
      { label: t(translationKeys.NOT_SET), id: ShortlistProfilePriority.NOT_SET, data: { color: '#E1E1E1' } },
      { label: t(translationKeys.HIGH), id: ShortlistProfilePriority.HIGH, data: { color: '#F27878' } },
      { label: t(translationKeys.MEDIUM), id: ShortlistProfilePriority.MEDIUM, data: { color: '#E9B737' } },
      { label: t(translationKeys.LOW), id: ShortlistProfilePriority.LOW, data: { color: '#8DADFF' } },
    ],
    [],
  )

  const isContactDetailsPopoverVisible =
    contactStatusDetailsPopoverRef &&
    contactStatusDetailsCompanyId &&
    !!companyContactsMap[contactStatusDetailsCompanyId]?.recentMeeting

  const userAttendingEvents = useMemo(
    () => user?.events.filter(event => event.isAttending).map(event => event.id) ?? [],
    [user?.events],
  )

  return (
    <Box sx={{ position: 'relative' }}>
      <Box ref={ref} sx={{ width: '100%', overflowX: 'auto', marginBottom: '-16px' }}>
        <TableWrapper>
          <TableContainer
            component={Paper}
            sx={{ position: 'relative', boxShadow: 'none', overflowX: 'inherit', paddingBottom: '15px' }}
          >
            <Table ref={tableElementRef}>
              {isPageDataLoading ? (
                <TableBody>
                  <TableRow>
                    <StyledTableCell>
                      <Skeleton variant="rectangular" sx={{ my: 1, mx: 1 }} />
                    </StyledTableCell>
                  </TableRow>
                  <TableRow>
                    <StyledTableCell>
                      <Skeleton variant="rectangular" sx={{ my: 1, mx: 1 }} />
                    </StyledTableCell>
                  </TableRow>
                  <TableRow>
                    <StyledTableCell>
                      <Skeleton variant="rectangular" sx={{ my: 1, mx: 1 }} />
                    </StyledTableCell>
                  </TableRow>
                </TableBody>
              ) : (
                <>
                  <TableHead>
                    <ShortlistOverviewTableColumnHeaders
                      activeShortlistId={activeShortlistId as number}
                      profiles={profilesPageData}
                      optionalColumnsConfig={optionalColumnsConfig}
                      allProfilesSelected={allProfilesSelected}
                      onSelectAllProfiles={onSelectAllProfiles}
                      sort={sort}
                      onSortChange={onSortChange}
                      tableColumns={tableColumns}
                      activeShortlist={activeShortlist}
                      handleEditCustomColumnSuccess={handleEditCustomColumnSuccess}
                      handleCloseEditCustomColumnPopover={handleCloseEditCustomColumnPopover}
                      onEditCustomColumnButtonClick={onEditCustomColumnButtonClick}
                      setCustomFieldSortId={setCustomFieldSortId}
                      customFieldSortId={customFieldSortId}
                      updateConfig={updateConfig}
                    />
                  </TableHead>
                  <TableBody sx={{ position: 'relative' }}>
                    {(profileAlerts || !hasAccessToAlerts) &&
                      profilesPageData.map(profileData =>
                        getShortlistOverviewTableColumns({
                          contactsOverview: companyContactsMap[profileData.companyId],
                          profileData,
                          optionalColumnsConfig,
                          selectedRowIds,
                          onContactStatusClick,
                          onSelectProfile,
                          onClickProfile,
                          onChangeNda,
                          onChangePriority,
                          onTagUpdate,
                          onNoteClick,
                          onCustomColumnClick,
                          onContactClick,
                          alertsShortlistId,
                          profileAlert: profilesAlertsMap[profileData.globalId],
                          tableColumns,
                          hasAccessToAlerts,
                          hasAccessToChat,
                          isEcommerce,
                          priorityOptions,
                          attendingEvents: userAttendingEvents,
                          t,
                          activeShortlist,
                        }),
                      )}
                  </TableBody>
                </>
              )}
            </Table>
          </TableContainer>
          {isContactDetailsPopoverVisible && (
            <ContactsHistoryPopover
              companyId={contactStatusDetailsCompanyId}
              recentMeeting={companyContactsMap[contactStatusDetailsCompanyId].recentMeeting as Meeting}
              anchor={contactStatusDetailsPopoverRef}
              setAnchor={onContactStatusClick}
            />
          )}
          {!!contactAttendeesPopoverData?.anchor && fetchedProfile && (
            <ContactAttendeesPopover
              anchor={contactAttendeesPopoverData?.anchor}
              profile={fetchedProfile}
              close={() => setContactAttendeesPopoverData(null)}
              openChatModal={openChatModal}
            />
          )}
        </TableWrapper>
      </Box>
    </Box>
  )
})

export default memo(ShortlistTable)
